import cn from 'classnames'
import { useMemo, useState } from 'react'

import { SegmentedControl } from 'cng-common/components/segmentedControl'
import { Translation, useSafetyTranslation } from 'cng-common/features/translations'
import { Input } from 'core-components/library/inputs'
import { getAttentionSeverityColor } from 'partner360/entities/attentionStatuses'

import { FINANCIAL_COCKPIT_TRANSLATION_KEYS } from '../../../../configs/translations'
import type {
  IAttentionNavigatorNode,
  IAttentionPartnerOverview,
  TAttentionNavigatorMode,
} from '../../../../lib'
import { CockpitNavigatorTreeRow } from '../../../components/cockpitNavigatorTreeRow'
import { CockpitSegmentedControlAccent } from '../../../components/cockpitSegmentedControlAccent'

import styles from './AttentionStatusesFullscreen.module.scss'

const KEYS = FINANCIAL_COCKPIT_TRANSLATION_KEYS.attentionStatuses.fullscreen

/** Partners the list draws at once; the search narrows the rest. */
const PARTNER_LIST_LIMIT = 200

interface IAttentionFullscreenNavigatorProps {
  modes: TAttentionNavigatorMode[]
  mode: TAttentionNavigatorMode
  onModeChange: (mode: TAttentionNavigatorMode) => void
  nodes: IAttentionNavigatorNode[]
  /** Partners the counters count: those with the picked status, or with anything open. */
  scopeIds: Set<string>
  selectedKey: string | null
  onSelect: (key: string | null) => void
  overviews: Map<string, IAttentionPartnerOverview>
  selectedPartnerId: string | null
  onSelectPartner: (partnerId: string | null) => void
}

const countIn = (partnerIds: string[], scopeIds: Set<string>) =>
  partnerIds.reduce((count, partnerId) => count + (scopeIds.has(partnerId) ? 1 : 0), 0)

const matches = (node: IAttentionNavigatorNode, query: string): boolean =>
  (node.label ?? '').toLowerCase().includes(query) ||
  (node.owner?.email.toLowerCase().includes(query) ?? false) ||
  node.children.some((child) => matches(child, query))

interface ITreeProps {
  nodes: IAttentionNavigatorNode[]
  depth: number
  scopeIds: Set<string>
  query: string
  selectedKey: string | null
  expandedKeys: Set<string>
  onToggle: (key: string) => void
  onSelect: (key: string) => void
}

const Tree = ({
  nodes,
  depth,
  scopeIds,
  query,
  selectedKey,
  expandedKeys,
  onToggle,
  onSelect,
}: ITreeProps) => {
  const t = useSafetyTranslation()
  const rows = nodes
    .map((node) => ({ node, count: countIn(node.partnerIds, scopeIds) }))
    .filter(({ node, count }) => (count > 0 || node.isHeading) && (!query || matches(node, query)))
    /* Headings keep their order; under them the rows with the most to do come first. */
    .sort((left, right) =>
      left.node.isHeading || right.node.isHeading ? 0 : right.count - left.count
    )

  if (rows.length === 0) {
    return null
  }

  return (
    <ul role={depth === 0 ? undefined : 'group'} className={styles.navList}>
      {rows.map(({ node, count }) => {
        const hasChildren = node.children.length > 0
        const isExpanded = Boolean(query) || node.isHeading || expandedKeys.has(node.key)

        if (node.isHeading) {
          return (
            <li key={node.key} role="none">
              <div className={styles.navHeading}>
                {node.labelKey && <Translation span text={node.labelKey} />}
              </div>
              <Tree
                nodes={node.children}
                depth={depth}
                scopeIds={scopeIds}
                query={query}
                selectedKey={selectedKey}
                expandedKeys={expandedKeys}
                onToggle={onToggle}
                onSelect={onSelect}
              />
            </li>
          )
        }

        return (
          <li key={node.key} role="treeitem" aria-expanded={hasChildren ? isExpanded : undefined}>
            <CockpitNavigatorTreeRow
              label={node.label}
              count={count}
              depth={depth}
              selected={node.key === selectedKey}
              expandable={hasChildren}
              expanded={isExpanded}
              toggleTitle={t(isExpanded ? KEYS.collapse : KEYS.expand)}
              onToggle={() => onToggle(node.key)}
              onSelect={() => onSelect(node.key)}
            />
            {hasChildren && isExpanded && (
              <Tree
                nodes={node.children}
                depth={depth + 1}
                scopeIds={scopeIds}
                query={query}
                selectedKey={selectedKey}
                expandedKeys={expandedKeys}
                onToggle={onToggle}
                onSelect={onSelect}
              />
            )}
          </li>
        )
      })}
    </ul>
  )
}

/**
 * Who or where the full-screen view looks at: people, places or the partners themselves, as the
 * role allows. Every counter counts the partners the picked status holds, so the rows with the
 * most to do stand out whichever cut is open.
 */
export const AttentionFullscreenNavigator = ({
  modes,
  mode,
  onModeChange,
  nodes,
  scopeIds,
  selectedKey,
  onSelect,
  overviews,
  selectedPartnerId,
  onSelectPartner,
}: IAttentionFullscreenNavigatorProps) => {
  const t = useSafetyTranslation()
  const [search, setSearch] = useState('')
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(() => new Set())
  const query = search.trim().toLowerCase()

  const partnerRows = useMemo(() => {
    if (mode !== 'partners') {
      return []
    }

    return [...overviews.values()]
      .filter(
        ({ partner }) =>
          scopeIds.has(partner.partnerId) &&
          (!query ||
            partner.partnerName.toLowerCase().includes(query) ||
            partner.country.toLowerCase().includes(query))
      )
      .sort(
        (left, right) =>
          right.worstSeverity - left.worstSeverity || right.open.length - left.open.length
      )
      .slice(0, PARTNER_LIST_LIMIT)
  }, [mode, overviews, query, scopeIds])

  const handleToggle = (key: string) =>
    setExpandedKeys((current) => {
      const next = new Set(current)

      if (next.has(key)) {
        next.delete(key)
      } else {
        next.add(key)
      }

      return next
    })

  const handleSelect = (key: string) => {
    setExpandedKeys((current) => new Set([...current, key]))
    onSelect(key)
  }

  const isRootSelected = mode === 'partners' ? !selectedPartnerId : !selectedKey

  return (
    <nav className={styles.side}>
      {modes.length > 1 && (
        <CockpitSegmentedControlAccent className={styles.navModes}>
          <SegmentedControl
            isStreatched
            useItemKeys
            selectedItem={mode}
            onItemSelect={(next: string) => {
              setSearch('')
              onModeChange(next as TAttentionNavigatorMode)
            }}
            size="small"
            variant="veneer"
          >
            {modes.map((option) => (
              <SegmentedControl.Item key={option} itemKey={option}>
                <Translation span text={KEYS.modes[option]} />
              </SegmentedControl.Item>
            ))}
          </SegmentedControl>
        </CockpitSegmentedControlAccent>
      )}
      <Input
        classes={{ root: styles.navSearch }}
        value={search}
        onChange={setSearch}
        startContent={<i className="kbase kbase-search" />}
        placeholder={t(KEYS.find[mode])}
      />
      <div className={cn(styles.navRow, { [styles.navRowSelected]: isRootSelected })}>
        <button
          type="button"
          className={styles.navLabel}
          onClick={() => (mode === 'partners' ? onSelectPartner(null) : onSelect(null))}
        >
          <span className={cn(styles.navName, styles.navRoot)}>
            <Translation span text={KEYS.root[mode]} />
          </span>
          <span className={styles.navCount}>{scopeIds.size}</span>
        </button>
      </div>
      {mode === 'partners' ? (
        <ul className={styles.navList}>
          {partnerRows.map(({ partner, open, worstSeverity }) => (
            <li key={partner.partnerId}>
              <div
                className={cn(styles.navRow, {
                  [styles.navRowSelected]: partner.partnerId === selectedPartnerId,
                })}
              >
                <button
                  type="button"
                  className={styles.navLabel}
                  onClick={() => onSelectPartner(partner.partnerId)}
                >
                  <span className={styles.navPartner}>
                    <span className={styles.navName}>{partner.partnerName}</span>
                    <span className={styles.navSub}>{partner.country}</span>
                  </span>
                  <span
                    className={styles.navBadge}
                    style={{ background: getAttentionSeverityColor(worstSeverity) }}
                  >
                    {open.length}
                  </span>
                </button>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <Tree
          nodes={nodes}
          depth={0}
          scopeIds={scopeIds}
          query={query}
          selectedKey={selectedKey}
          expandedKeys={expandedKeys}
          onToggle={handleToggle}
          onSelect={handleSelect}
        />
      )}
    </nav>
  )
}

AttentionFullscreenNavigator.displayName = 'AttentionFullscreenNavigator'
